/**
 * Proposal Management Hook
 * Handles proposal operations (save, delete, load scenarios)
 */

import { useState, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';

export const useProposalManagement = () => {
  const [selectedProposalId, setSelectedProposalId] = useState(null);
  const [proposalScenarios, setProposalScenarios] = useState([]);
  const [selectedScenarios, setSelectedScenarios] = useState(new Set());

  /**
   * Load scenarios for a proposal
   */
  const loadProposalScenarios = useCallback(async (collectionId) => {
    if (!collectionId) {
      setProposalScenarios([]);
      return;
    }

    try {
      const response = await api.get(`/collections/${collectionId}`);
      const scenarios = response.data.scenarios || [];
      setProposalScenarios(scenarios);
      console.log('[ProposalManagement] Loaded scenarios:', scenarios.length);
    } catch (error) {
      console.error('[ProposalManagement] Error loading scenarios:', error);
      toast.error('Failed to load proposal scenarios');
      setProposalScenarios([]);
    }
  }, []);

  /**
   * Save calculation to proposal
   * If editing an existing scenario (scenario_id provided), UPDATE it
   * Otherwise, CREATE a new scenario
   */
  const saveToProposal = useCallback(async (calculationData, proposalId) => {
    try {
      if (!calculationData) {
        toast.error('No calculation to save. Please calculate first.');
        return false;
      }
      
      if (!proposalId) {
        toast.error('No proposal selected. Please select a proposal first.');
        return false;
      }

      let scenarioId = calculationData.scenario_id;
      
      // If scenario exists, UPDATE it instead of creating a new one
      if (scenarioId) {
        console.log('[ProposalManagement] Updating existing scenario:', scenarioId);
        
        const updatePayload = {
          name: calculationData.scenario_name || `${calculationData.service_name || 'Calculation'} - ${new Date().toLocaleDateString()}`,
          service_name: calculationData.service_name,
          scenario_info: calculationData.scenario_info,
          volume_assumptions: calculationData.volume_assumptions,
          incumbent_pricing: calculationData.incumbent_pricing,
          proposed_pricing: calculationData.proposed_pricing,
          transaction_distribution: calculationData.transaction_distribution,
          calculation_results: calculationData.calculation_results || calculationData,
          profitability_results: calculationData.profitability_results || calculationData,
          reader_cost_recovery: calculationData.reader_cost_recovery
        };
        
        await api.put(`/scenarios/${scenarioId}`, updatePayload);
        console.log('[ProposalManagement] Updated scenario:', scenarioId);
        toast.success(`✅ Calculation updated successfully!`);
        
      } else {
        // Create new scenario
        console.log('[ProposalManagement] Creating new scenario for calculation...');
        
        const scenarioPayload = {
          name: calculationData.scenario_name || `${calculationData.service_name || 'Calculation'} - ${new Date().toLocaleDateString()}`,
          service_name: calculationData.service_name,
          scenario_info: calculationData.scenario_info,
          volume_assumptions: calculationData.volume_assumptions,
          incumbent_pricing: calculationData.incumbent_pricing,
          proposed_pricing: calculationData.proposed_pricing,
          transaction_distribution: calculationData.transaction_distribution,
          calculation_results: calculationData.calculation_results || calculationData,
          profitability_results: calculationData.profitability_results || calculationData,
          reader_cost_recovery: calculationData.reader_cost_recovery,
          collection_id: proposalId,
          status: 'draft'
        };
        
        const createResponse = await api.post('/scenarios', scenarioPayload);
        scenarioId = createResponse.data.scenario.id;
        console.log('[ProposalManagement] Created scenario:', scenarioId);
        toast.success(`💼 Calculation saved to proposal!`);
      }
      
      // Reload proposal scenarios
      await loadProposalScenarios(proposalId);
      
      return true;
    } catch (error) {
      console.error('[ProposalManagement] Error saving to proposal:', error);
      toast.error(error.response?.data?.detail || 'Failed to save to proposal');
      return false;
    }
  }, [loadProposalScenarios]);

  /**
   * Delete scenario from proposal
   */
  const deleteScenario = useCallback(async (collectionId, scenarioId) => {
    try {
      await api.delete(`/collections/${collectionId}/scenarios/${scenarioId}`);
      toast.success('Calculation removed from proposal');
      
      // Reload proposal scenarios
      await loadProposalScenarios(collectionId);
      
      return true;
    } catch (error) {
      console.error('[ProposalManagement] Error deleting scenario:', error);
      toast.error('Failed to delete calculation');
      return false;
    }
  }, [loadProposalScenarios]);

  /**
   * Handle proposal selection
   */
  const handleProposalSelect = useCallback(async (proposalId) => {
    setSelectedProposalId(proposalId);
    if (proposalId) {
      await loadProposalScenarios(proposalId);
    } else {
      setProposalScenarios([]);
    }
  }, [loadProposalScenarios]);

  /**
   * Toggle scenario selection for bulk operations
   */
  const toggleScenarioSelection = useCallback((scenarioId) => {
    setSelectedScenarios(prev => {
      const newSet = new Set(prev);
      if (newSet.has(scenarioId)) {
        newSet.delete(scenarioId);
      } else {
        newSet.add(scenarioId);
      }
      return newSet;
    });
  }, []);

  /**
   * Clear all scenario selections
   */
  const clearScenarioSelection = useCallback(() => {
    setSelectedScenarios(new Set());
  }, []);

  return {
    // State
    selectedProposalId,
    proposalScenarios,
    selectedScenarios,
    
    // Actions
    loadProposalScenarios,
    saveToProposal,
    deleteScenario,
    handleProposalSelect,
    toggleScenarioSelection,
    clearScenarioSelection,
    setSelectedProposalId,
    setProposalScenarios
  };
};
